import React, { MouseEvent, useState, useEffect } from 'react';
import {
  Card,
  Flex,
  Button,
  Select,
  TextField,
  IconButton,
  Text,
} from '@radix-ui/themes';
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';

import {
  LogFingerprint,
  FingerprintValue,
  FingerprintValueType,
  ConditionOperator,
  LogField,
  SlotDataType,
} from '../../types';

import TopicFieldInput from '@/components/WorkflowBuilder/inputs/TopicFieldInput';
import {
  getOperationsForSlotDataType,
  filterNullOperations,
  formatOperator,
} from './operationsByValueType';

// Log field attributes with their types
export const logAttributes: {
  key: LogField;
  label: string;
  type: SlotDataType;
  nullable: boolean;
}[] = [
  {
    key: LogField.Address,
    label: 'Address',
    type: SlotDataType.Address,
    nullable: false,
  },
  {
    key: LogField.Topic_0,
    label: 'Topic 0',
    type: SlotDataType.Topic,
    nullable: false, // Event signature is always present
  },
  {
    key: LogField.Topic_1,
    label: 'Topic 1',
    type: SlotDataType.Topic,
    nullable: true, // Indexed params may not exist
  },
  {
    key: LogField.Topic_2,
    label: 'Topic 2',
    type: SlotDataType.Topic,
    nullable: true,
  },
  {
    key: LogField.Topic_3,
    label: 'Topic 3',
    type: SlotDataType.Topic,
    nullable: true,
  },
  {
    key: LogField.Data,
    label: 'Data',
    type: SlotDataType.Bytes,
    nullable: false, // Always present (can be empty)
  },
  {
    key: LogField.BlockHash,
    label: 'Block Hash',
    type: SlotDataType.String,
    nullable: false,
  },
  {
    key: LogField.BlockNumber,
    label: 'Block Number',
    type: SlotDataType.Number,
    nullable: false,
  },
  {
    key: LogField.TransactionHash,
    label: 'Transaction Hash',
    type: SlotDataType.String,
    nullable: false,
  },
  {
    key: LogField.TransactionIndex,
    label: 'Transaction Index',
    type: SlotDataType.Number,
    nullable: false,
  },
  {
    key: LogField.LogIndex,
    label: 'Log Index',
    type: SlotDataType.Number,
    nullable: false,
  },
];

const TextFieldInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <TextField.Root value={value} onChange={(e) => onChange(e.target.value)} />
  );
};

const ArrayInput = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const [items, setItems] = useState<string[]>(
    Array.isArray(value) ? value : [],
  );

  const addItem = () => {
    const newItems = [...items, ''];
    setItems(newItems);
    onChange(newItems);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onChange(newItems);
  };

  const updateItem = (index: number, newValue: string) => {
    const newItems = [...items];
    newItems[index] = newValue;
    setItems(newItems);
    onChange(newItems);
  };

  return (
    <Flex direction="column" gap="2">
      {items.map((item, index) => (
        <Flex key={index} gap="2" align="center">
          <TextField.Root
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder="0x1234... (hex value)"
            style={{ flex: 1 }}
          />
          <IconButton
            size="1"
            variant="ghost"
            color="red"
            onClick={() => removeItem(index)}
          >
            <TrashIcon />
          </IconButton>
        </Flex>
      ))}
      <Button
        size="1"
        variant="outline"
        onClick={addItem}
        style={{ alignSelf: 'flex-start' }}
      >
        <PlusIcon />
        Add Item
      </Button>
    </Flex>
  );
};

// Range input for Between/NotBetween with numbers
const NumberRangeInput = ({
  value,
  onChange,
}: {
  value: { min: string; max: string };
  onChange: (value: { min: string; max: string }) => void;
}) => {
  const rangeValue = value || { min: '', max: '' };
  return (
    <Flex direction="row" gap="2" align="center">
      <TextField.Root
        type="number"
        placeholder="Min"
        value={rangeValue.min}
        onChange={(e) => onChange({ ...rangeValue, min: e.target.value })}
        style={{ flex: 1 }}
      />
      <Text size="2" color="gray">
        and
      </Text>
      <TextField.Root
        type="number"
        placeholder="Max"
        value={rangeValue.max}
        onChange={(e) => onChange({ ...rangeValue, max: e.target.value })}
        style={{ flex: 1 }}
      />
    </Flex>
  );
};

// Check if operation is a range operation
const isRangeOperation = (op: ConditionOperator | undefined): boolean =>
  op === ConditionOperator.Between || op === ConditionOperator.NotBetween;

// Map for attribute types to input components
const attributeTypeComponentMap: Record<
  SlotDataType,
  React.ComponentType<any>
> = {
  [SlotDataType.String]: TextFieldInput,
  [SlotDataType.Number]: TextFieldInput,
  [SlotDataType.Array]: ArrayInput,
  [SlotDataType.Bytes]: TextFieldInput,
  [SlotDataType.Address]: TextFieldInput,
  [SlotDataType.Object]: TextFieldInput,
  [SlotDataType.Topic]: TopicFieldInput,
};

const defaultFingerprint: Partial<LogFingerprint> = {
  field: logAttributes[0].key,
  operation: ConditionOperator.Equals,
  value: undefined,
};

export interface LogFingerprintEditorProps {
  initialFingerprint?: LogFingerprint;
  onSave: (fingerprint: LogFingerprint) => void;
  onCancel: () => void;
}

const LogFingerprintEditor = ({
  initialFingerprint,
  onSave,
  onCancel,
}: LogFingerprintEditorProps) => {
  const [fingerprint, setFingerprint] = useState<Partial<LogFingerprint>>(
    initialFingerprint || defaultFingerprint,
  );

  // Get the selected attribute to determine the input type
  const selectedAttributeInfo = logAttributes.find(
    (attr) => attr.key === fingerprint.field,
  );
  const isNullable = selectedAttributeInfo?.nullable ?? false;

  // Get valid operations for this slot data type, filtering null ops if not nullable
  const validOperations = filterNullOperations(
    getOperationsForSlotDataType(
      selectedAttributeInfo?.type ?? SlotDataType.String,
    ),
    isNullable,
  );

  // Reset operation when field changes if current operation is not valid
  useEffect(() => {
    if (
      fingerprint.operation &&
      !validOperations.includes(fingerprint.operation)
    ) {
      setFingerprint((prev: Partial<LogFingerprint>) => ({
        ...prev,
        operation: validOperations[0],
      }));
    }
  }, [fingerprint.field, fingerprint.operation, validOperations]);

  // Reset value when switching between range and non-range operations
  useEffect(() => {
    const needsRange = isRangeOperation(fingerprint.operation);
    const hasRange =
      fingerprint.value?.value &&
      typeof fingerprint.value.value === 'object' &&
      'min' in fingerprint.value.value;

    if (needsRange !== hasRange) {
      setFingerprint((prev) => ({
        ...prev,
        value: {
          value: needsRange ? { min: '', max: '' } : '',
          valueType: prev.value?.valueType || FingerprintValueType.String,
        },
      }));
    }
  }, [fingerprint.operation]);

  const handleSaveClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();

    if (fingerprint.value) {
      onSave(fingerprint as LogFingerprint);
    }
  };

  const ValueFieldComponent =
    attributeTypeComponentMap[
      selectedAttributeInfo?.type ?? SlotDataType.String
    ];

  return (
    <Card size="1">
      <Flex direction="column" gap="2">
        <Select.Root
          value={fingerprint.field}
          onValueChange={(val) =>
            setFingerprint({ ...fingerprint, field: val as LogField })
          }
        >
          <Select.Trigger />
          <Select.Content>
            {logAttributes.map((attr) => (
              <Select.Item key={attr.key} value={attr.key}>
                {attr.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Select.Root
          value={fingerprint.operation}
          onValueChange={(value) =>
            setFingerprint({
              ...fingerprint,
              operation: value as ConditionOperator,
            })
          }
        >
          <Select.Trigger />
          <Select.Content>
            {validOperations.map((op: ConditionOperator) => (
              <Select.Item key={op} value={op}>
                {formatOperator(op)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {isRangeOperation(fingerprint.operation) &&
        selectedAttributeInfo?.type === SlotDataType.Number ? (
          <NumberRangeInput
            value={fingerprint.value?.value}
            onChange={(newValue) => {
              setFingerprint({
                ...fingerprint,
                value: {
                  value: newValue,
                  valueType: FingerprintValueType.Number,
                },
              });
            }}
          />
        ) : (
          <ValueFieldComponent
            value={fingerprint.value?.value}
            onChange={(newValue: FingerprintValue['value']) => {
              setFingerprint({
                ...fingerprint,
                value: {
                  value: newValue,
                  valueType:
                    fingerprint.value?.valueType || FingerprintValueType.String,
                },
              });
            }}
          />
        )}
        <Flex gap="2" justify="between">
          <Button onClick={onCancel} variant="soft">
            Cancel
          </Button>
          <Button onClick={handleSaveClick}>
            {initialFingerprint ? 'Update Condition' : 'Save Condition'}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

export default LogFingerprintEditor;
